<template>
  <div
    class="video-processing-wrapper"
    ref="pageScroll"
    @touchstart="onOuterTouchStart"
    @touchmove="onOuterTouchMove"
  >
    <div class="page-inner">
      <div class="video-processing">
        <h2 class="center-align">视频处理</h2>

        <div class="content-scroll">
          <div class="content-area">
            <div class="left-area">
              <div
                class="video-container"
                @mouseenter="showControls = true"
                @mouseleave="showControls = false"
              >
                <div
                  v-if="!selectedFile"
                  class="upload-box"
                  @click="triggerFileUpload"
                  @drop.prevent="handleFileDrop"
                  @dragover.prevent
                >
                  <div class="upload-placeholder">
                    <div class="upload-icon">+</div>
                    <p>点击上传或拖拽视频</p>
                    <small>支持mp4、avi、mov、wmv格式,大小不超过500MB</small>
                    <small>视频超分辨率、生成字幕、视频抠图、视频转换</small>
                    <input
                      type="file"
                      ref="fileInput"
                      @change="handleFileSelect"
                      accept="video/*"
                      style="display: none;"
                    >
                  </div>
                </div>

                <div v-else class="video-wrapper">
                  <span v-show="showControls" class="file-name">{{ selectedFile.name }}</span>
                  <button v-show="showControls" class="delete-btn" @click.stop="replaceFile">×</button>
                  <video :src="videoUrl" controls></video>
                </div>
              </div>
            </div>

            <div class="right-area">
              <div class="option-container">
                <div class="process-selection">
                  <label>视频处理选项</label>
                  <select v-model="processOption">
                    <option value="videoUpscaleSR">视频清晰</option>
                    <option value="videoMatting">视频抠图</option>
                    <option value="videoSubtitle">生成字幕</option>
                    <option value="videoConvert">视频转换</option>
                  </select>
                </div>

                <div v-if="processOption === 'videoSubtitle'" class="subtitle-options">
                  <label>字幕格式:</label>
                  <select v-model="subtitleFormat">
                    <option value="srt">SRT</option>
                    <option value="txt">TXT</option>
                    <option value="vtt">VTT</option>
                    <option value="ass">ASS</option>
                    <option value="ssa">SSA</option>
                  </select>
                </div>

                <div v-if="processOption === 'videoUpscaleSR'" class="upscale-selection">
                  <label>放大倍数</label>
                  <div class="upscale-options">
                    <label v-for="factor in [1, 2, 4, 8]" :key="factor" class="upscale-radio">
                      <input
                        type="radio"
                        v-model="upscaleFactor"
                        :value="factor"
                      />
                      {{ factor }}x
                    </label>
                  </div>
                </div>

                <div v-if="processOption === 'videoConvert'" class="convert-options">
                  <label>目标文件格式:</label>
                  <select v-model="outputFormat">
                    <option value="mp4">MP4</option>
                    <option value="avi">AVI</option>
                    <option value="mov">MOV</option>
                    <option value="wmv">WMV</option>
                    <option value="mkv">MKV</option>
                    <option value="flv">FLV</option>
                    <option value="webm">WEBM</option>
                    <option value="m4v">M4V</option>
                    <option value="3gp">3GP</option>
                  </select>
                </div>
              </div>

              <div v-if="selectedFile" class="billing-info">
                <div class="billing-header">
                  <span class="billing-icon">💰</span>
                  <span class="billing-title">计费说明</span>
                </div>
                <div class="billing-content">
                  <div class="billing-item">
                    <span class="operation-name">{{ getCurrentOperationName() }}</span>
                    <span class="price">{{ getCurrentPrice() }}/分钟</span>
                  </div>
                  <div class="billing-detail">
                    预估视频时长:{{ getFormattedDuration() }}<br>
                    预估费用:{{ getEstimatedCost() }}
                  </div>
                </div>
              </div>

              <button
                class="process-button"
                @click="startProcessing"
                :disabled="!selectedFile || processing"
              >
                {{ processing ? '处理中...' : '开始处理' }}
              </button>
            </div>
          </div>
        </div>

        <p class="terms">
          仅对本人视频进行处理,您承诺您的素材内容为您本人所有或已获得合法授权。并遵守《视频处理功能使用规范》
        </p>
      </div>

      <div class="user-records">
        <h3>用户操作记录</h3>

        <div class="filter-container">
          <div class="filter-item">
            <label class="filter-label">筛选类型:</label>
            <select v-model="selectedRecordType" class="record-type-filter">
              <option value="videoSubtitle">生成字幕</option>
              <option value="videoUpscaleSR">视频清晰</option>
              <option value="videoMatting">视频抠图</option>
              <option value="videoConvert">视频转换</option>
            </select>
          </div>

          <div class="filter-item">
            <label class="filter-label">日期:</label>
            <input
              v-model="selectedDate"
              type="date"
              class="date-filter"
            />
          </div>

          <button @click="queryRecords" class="query-button">查询</button>
        </div>

        <div
          class="table-scroll"
          ref="tableScroll"
          @touchstart.stop="onTableTouchStart"
          @touchmove.stop="onTableTouchMove"
        >
          <div class="table-container">
            <table>
              <thead>
                <tr>
                  <th>文件名</th>
                  <th>上传时间</th>
                  <th>处理状态</th>
                  <th>下载</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(record, index) in userRecords" :key="index">
                  <td class="table-cell-center">{{ record.input_filename }}</td>
                  <td class="table-cell-center">{{ formatUploadTime(record.upload_time) }}</td>
                  <td class="table-cell-center">{{ record.task_status }}</td>
                  <td class="table-cell-center">
                    <a href="#" @click.prevent="downloadFile(record)">下载</a>
                  </td>
                  <td class="table-cell-center">
                    <button @click="deleteRecord(record)">删除</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <transition name="toast-fade">
        <div v-if="toast.visible" class="toast-container" :class="toast.type">
          <div class="toast-content">
            <span class="toast-icon">{{ getToastIcon() }}</span>
            <span class="toast-message">{{ toast.message }}</span>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { getUpQiniuToken, savevidioAdjustFileUrl, uploadToQiniu, getUserRecords, deleteUserRecord } from '@/api/upfile'
import { QINIU_BASE_URL } from '@/api/constants'
import { v4 as uuidv4 } from 'uuid'
import { mapGetters } from 'vuex'

export default {
  name: 'VideoProcessing',
  data () {
    return {
      processing: false,
      processOption: 'videoMatting',
      selectedFile: null,
      videoUrl: '',
      fileUrl: '',
      showControls: false,
      subtitleFormat: 'srt',
      upscaleFactor: 2,
      outputFormat: 'mp4',
      videoDuration: 0,
      userRecords: [],
      selectedRecordType: 'videoMatting',
      selectedDate: '',
      toast: {
        visible: false,
        message: '',
        type: 'info'
      },
      outerTouchStartX: 0,
      outerTouchStartY: 0,
      tableTouchStartX: 0,
      tableTouchStartY: 0
    }
  },
  computed: {
    ...mapGetters(['balance', 'isLoggedIn']),
    estimatedCost () {
      if (this.videoDuration === 0) return 0
      const minutes = Math.ceil(this.videoDuration / 60)
      return parseFloat((minutes * 1.0).toFixed(2))
    },
    canProcess () {
      return this.selectedFile && this.balance >= this.estimatedCost
    }
  },
  beforeDestroy () {
    if (this.videoUrl) {
      URL.revokeObjectURL(this.videoUrl)
    }
  },
  methods: {
    onOuterTouchStart (e) {
      if (!e.touches || !e.touches.length) return
      this.outerTouchStartX = e.touches[0].clientX
      this.outerTouchStartY = e.touches[0].clientY
    },

    onOuterTouchMove (e) {
      const el = this.$refs.pageScroll
      if (!el || !e.touches || !e.touches.length) return

      const currentX = e.touches[0].clientX
      const currentY = e.touches[0].clientY
      const deltaX = currentX - this.outerTouchStartX
      const deltaY = currentY - this.outerTouchStartY

      if (Math.abs(deltaX) <= Math.abs(deltaY)) return

      const atLeftEdge = el.scrollLeft <= 0
      const maxScrollLeft = el.scrollWidth - el.clientWidth
      const atRightEdge = el.scrollLeft >= maxScrollLeft - 1

      if ((atLeftEdge && deltaX > 0) || (atRightEdge && deltaX < 0)) {
        e.preventDefault()
      }

      e.stopPropagation()
    },

    onTableTouchStart (e) {
      if (!e.touches || !e.touches.length) return
      this.tableTouchStartX = e.touches[0].clientX
      this.tableTouchStartY = e.touches[0].clientY
    },

    onTableTouchMove (e) {
      const el = this.$refs.tableScroll
      if (!el || !e.touches || !e.touches.length) return

      const currentX = e.touches[0].clientX
      const currentY = e.touches[0].clientY
      const deltaX = currentX - this.tableTouchStartX
      const deltaY = currentY - this.tableTouchStartY

      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        const atLeftEdge = el.scrollLeft <= 0
        const maxScrollLeft = el.scrollWidth - el.clientWidth
        const atRightEdge = el.scrollLeft >= maxScrollLeft - 1

        if ((atLeftEdge && deltaX > 0) || (atRightEdge && deltaX < 0)) {
          e.preventDefault()
        }
      }

      e.stopPropagation()
    },

    showToast (message, type = 'info', duration = 3000) {
      this.toast.message = message
      this.toast.type = type
      this.toast.visible = true
      setTimeout(() => {
        this.toast.visible = false
      }, duration)
    },

    getToastIcon () {
      const icons = {
        success: '✓',
        error: '✕',
        warning: '⚠',
        info: 'ℹ'
      }
      return icons[this.toast.type] || 'ℹ'
    },

    // formatUploadTime (time) {
    //   if (!time) return ''
    //  const d = new Date(time)
    //  if (isNaN(d)) return time
    //  const pad = n => n.toString().padStart(2, '0')
    //  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
    // },
    formatUploadTime (time) {
      if (!time) return ''
      const date = new Date(time)
      if (isNaN(date.getTime())) return time

      // 获取 UTC 时间戳，加上 8 小时（东八区偏移）
      const beijingTimestamp = date.getTime()
      const beijingDate = new Date(beijingTimestamp)

      const pad = n => n.toString().padStart(2, '0')
      return `${beijingDate.getUTCFullYear()}-${pad(beijingDate.getUTCMonth() + 1)}-${pad(beijingDate.getUTCDate())} ${pad(beijingDate.getUTCHours())}:${pad(beijingDate.getUTCMinutes())}:${pad(beijingDate.getUTCSeconds())}`
    },

    triggerFileUpload () {
      this.$refs.fileInput.click()
    },

    handleFileSelect (event) {
      const file = event.target.files[0]
      if (file && file.type.startsWith('video/')) {
        if (this.videoUrl) {
          URL.revokeObjectURL(this.videoUrl)
        }
        this.selectedFile = file
        this.videoUrl = URL.createObjectURL(file)
        this.getVideoDuration()
      } else {
        this.showToast('请选择有效的视频文件', 'error')
      }
    },

    handleFileDrop (event) {
      const file = event.dataTransfer.files[0]
      if (file && file.type.startsWith('video/')) {
        if (this.videoUrl) {
          URL.revokeObjectURL(this.videoUrl)
        }
        this.selectedFile = file
        this.videoUrl = URL.createObjectURL(file)
        this.getVideoDuration()
      } else {
        this.showToast('请拖拽有效的视频文件', 'error')
      }
    },

    replaceFile () {
      if (this.videoUrl) {
        URL.revokeObjectURL(this.videoUrl)
      }
      this.selectedFile = null
      this.videoUrl = ''
      this.videoDuration = 0
      this.triggerFileUpload()
    },

    getVideoDuration () {
      this.$nextTick(() => {
        const video = this.$el.querySelector('video')
        if (video) {
          video.onloadedmetadata = () => {
            this.videoDuration = video.duration
          }
        }
      })
    },

    getCurrentOperationName () {
      const operationMap = {
        videoSubtitle: '生成字幕',
        videoUpscaleSR: '视频清晰',
        videoMatting: '视频抠图',
        videoConvert: '视频转换'
      }
      return operationMap[this.processOption] || '未知操作'
    },

    getCurrentPrice () {
      return this.estimatedCost > 0
        ? (this.estimatedCost / Math.ceil(this.videoDuration / 60)).toFixed(1) + '元'
        : '0元'
    },

    getFormattedDuration () {
      return this.videoDuration > 0 ? `${Math.ceil(this.videoDuration / 60)}分钟` : '未知'
    },

    getEstimatedCost () {
      return this.videoDuration > 0 ? `${this.estimatedCost}元` : '计算中...'
    },

    async startProcessing () {
      if (!this.isLoggedIn) {
        this.showToast('请先登录', 'error')
        return
      }

      if (!this.selectedFile) {
        this.showToast('请先选择一个视频文件', 'warning')
        return
      }

      if (this.balance < this.estimatedCost) {
        this.$message?.error('账户余额不足,请先充值') || this.showToast('账户余额不足,请先充值', 'error')
        return
      }

      this.processing = true

      const file = this.selectedFile
      const fileExtension = file.name.split('.').pop()
      const uniqueFilename = `${uuidv4().replace(/-/g, '')}.${fileExtension}`
      const key = uniqueFilename

      try {
        const tokenResponse = await getUpQiniuToken()
        const qiniuUploadToken = tokenResponse.uploadToken
        const response = await uploadToQiniu(file, key, qiniuUploadToken)
        this.fileUrl = `${QINIU_BASE_URL}/${response.data.key}`

        const data = {
          input_file_url: this.fileUrl,
          input_filename: key,
          operation_type: this.processOption,
          video_duration: this.videoDuration
        }

        if (this.processOption === 'videoSubtitle') {
          data.subtitle_format = this.subtitleFormat
        } else if (this.processOption === 'videoUpscaleSR') {
          data.repair_options = { upscale_factor: this.upscaleFactor }
        } else if (this.processOption === 'videoConvert') {
          data.output_format = this.outputFormat
        }

        const processResponse = await savevidioAdjustFileUrl(data)

        if (processResponse.status === 200) {
          this.$message?.success('视频处理已开始') || this.showToast('视频处理已开始', 'success')

          if (typeof processResponse.balance !== 'undefined') {
            this.$store.commit('setBalance', processResponse.balance)
          }

          if (Array.isArray(processResponse.result)) {
            this.userRecords = processResponse.result.map(record => ({
              ...record,
              upload_time: record.upload_time
            })).sort((a, b) => new Date(b.upload_time) - new Date(a.upload_time))
          }

          if (this.videoUrl) {
            URL.revokeObjectURL(this.videoUrl)
          }
          this.selectedFile = null
          this.videoUrl = ''
          this.videoDuration = 0
        } else {
          this.showToast(processResponse.message || '提交失败', 'error')
        }
      } catch (error) {
        this.showToast('抱歉,处理过程中出现错误,请稍后重试', 'error')
      } finally {
        this.processing = false
      }
    },

    async fetchUserRecords () {
      try {
        const data = {
          table_names: [this.selectedRecordType]
        }
        if (this.selectedDate) {
          data.date = this.selectedDate
        }
        const response = await getUserRecords(data)
        this.userRecords = (response.result || []).sort((a, b) => {
          return new Date(b.upload_time) - new Date(a.upload_time)
        })
      } catch (error) {
        this.showToast('获取用户记录失败', 'error')
      }
    },

    queryRecords () {
      this.fetchUserRecords()
    },

    async deleteRecord (record) {
      try {
        const payload = {
          operation_type: record.operation_type,
          phoneNum: record.user_phone || record.phoneNum,
          taskId: record.task_id
        }
        const response = await deleteUserRecord(payload)
        if (response.status === 200) {
          this.showToast('删除任务成功', 'success')
        } else {
          this.showToast(`删除任务失败: ${response.message}`, 'error')
        }
        this.userRecords = this.userRecords.filter(r => r.task_id !== record.task_id)
      } catch (error) {
        this.showToast('删除记录失败', 'error')
      }
    },

    downloadFile (record) {
      if (record.task_status === 'completed' && record.result_file_url) {
        const link = document.createElement('a')
        link.href = record.result_file_url
        link.download = record.input_filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } else {
        this.showToast('文件处理尚未完成或处理失败,无法下载', 'warning')
      }
    }
  }
}
</script>

<style scoped>
*,
*::before,
*::after {
  box-sizing: border-box;
}

.video-processing-wrapper {
  width: 780px;
  min-height: 100vh;
  background-color: #1a1a1a;
  overflow-x: auto;
  overflow-y: visible;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
  overscroll-behavior-y: auto;
  touch-action: pan-x pan-y;
  position: relative;
}

.page-inner {
  min-width: 780px;
  width: max-content;
  margin: 0 auto;
  padding: 0;
}

.video-processing {
  font-family: Arial, sans-serif;
  width: 750px;
  min-width: 750px;
  margin: 0 auto;
  padding: 15px;
  color: white;
  background-color: #1a1a1a;
}

h2 {
  margin-bottom: 10px;
}

.center-align {
  text-align: center;
}

.content-scroll {
  width: 100%;
  overflow: visible;
}

.content-area {
  display: flex;
  gap: 20px;
  width: 720px;
  min-width: 720px;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: flex-start;
}

.left-area {
  width: 400px;
  height: 350px;
  flex-shrink: 0;
  flex-grow: 0;
}

.right-area {
  width: 300px;
  height: 350px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-shrink: 0;
  flex-grow: 0;
}

.option-container {
  flex-grow: 1;
  overflow-y: auto;
  margin-bottom: 15px;
}

.video-container {
  width: 100%;
  height: 100%;
  background-color: #2a2a2a;
  border-radius: 8px;
  position: relative;
}

.upload-box {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
  height: 100%;
  width: 100%;
  text-align: center;
  padding: 10px;
}

.upload-icon {
  font-size: 32px;
  color: #00bcd4;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
}

.file-name {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 10;
  color: white;
  background-color: rgba(0, 0, 0, 0.5);
  padding: 5px;
  border-radius: 4px;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 70%;
}

.delete-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 10;
  background-color: #ff4d4d;
  color: white;
  border: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
}

.delete-btn:hover {
  background-color: #ff3333;
}

video {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.process-selection,
.subtitle-options,
.convert-options {
  margin-bottom: 15px;
}

.process-selection label,
.subtitle-options label,
.convert-options label {
  display: block;
  margin-bottom: 4px;
  font-size: 14px;
}

select {
  width: 100%;
  padding: 6px;
  background-color: #333;
  color: white;
  border: 1px solid #555;
  border-radius: 2px;
}

.process-button {
  width: 100%;
  padding: 6px;
  background-color: #00bcd4;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-top: auto;
}

.process-button:disabled {
  background-color: #555;
  cursor: not-allowed;
}

.billing-info {
  background: linear-gradient(135deg, #2a2a2a 0%, #333 100%);
  border: 1px solid #444;
  border-radius: 8px;
  padding: 8px;
  margin-bottom: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.billing-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.billing-icon {
  font-size: 16px;
  margin-right: 6px;
}

.billing-title {
  font-size: 14px;
  font-weight: bold;
  color: #00bcd4;
}

.billing-content {
  font-size: 12px;
  color: #ccc;
}

.billing-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.operation-name {
  font-weight: 500;
}

.price {
  color: #ff9800;
  font-weight: bold;
}

.billing-detail {
  padding-top: 6px;
  border-top: 1px solid #444;
  line-height: 1.4;
}

.terms {
  font-size: 12px;
  color: #888;
  margin-top: 20px;
}

.upscale-selection {
  margin-bottom: 15px;
}

.upscale-options {
  display: flex;
  gap: 15px;
  margin-top: 5px;
}

.upscale-radio {
  display: flex;
  align-items: center;
  gap: 4px;
}

.user-records {
  margin-top: 20px;
  background-color: #2a2a2a;
  padding: 10px;
  border-radius: 8px;
  width: 750px;
  min-width: 750px;
  margin-left: auto;
  margin-right: auto;
  font-family: Arial, sans-serif;
  color: white;
}

.user-records h3 {
  color: white;
  margin-bottom: 15px;
  text-align: center;
}

.filter-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 15px;
  gap: 15px;
  flex-wrap: wrap;
}

.filter-item {
  display: flex;
  align-items: center;
}

.filter-label {
  margin-right: 5px;
  font-size: 12px;
}

.record-type-filter,
.date-filter {
  padding: 2px;
  background-color: #333;
  color: white;
  border: 1px solid #555;
  border-radius: 4px;
}

.record-type-filter {
  width: 200px;
}

.date-filter {
  width: 150px;
}

.query-button {
  padding: 4px 16px;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.query-button:hover {
  background-color: #388E3C;
}

.table-scroll {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
  touch-action: pan-x;
}

.table-container {
  min-width: 100%;
}

table {
  width: 100%;
  min-width: 720px;
  border-collapse: collapse;
  color: white;
}

th,
td {
  border: 1px solid #444;
  padding: 3px;
  text-align: center;
  white-space: nowrap;
}

th {
  background-color: #333;
  font-weight: bold;
}

tr:nth-child(even) {
  background-color: #3a3a3a;
}

a {
  color: #00bcd4;
  text-decoration: none;
}

a:hover {
  text-decoration: underline;
}

.user-records button {
  color: white;
  border: none;
  padding: 2px 10px;
  border-radius: 4px;
  cursor: pointer;
  background-color: #666;
}

.user-records button:hover {
  background-color: #4CAF50;
}

.table-cell-center {
  text-align: center;
}

.toast-container {
  position: fixed !important;
  top: 80px !important;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10001 !important;
  padding: 12px 24px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  min-width: 300px;
  max-width: 90vw;
}

.toast-content {
  display: flex;
  align-items: center;
  gap: 10px;
}

.toast-icon {
  font-size: 20px;
  font-weight: bold;
}

.toast-message {
  font-size: 14px;
  font-weight: 500;
}

.toast-container.success {
  background-color: #4caf50;
  color: white;
}

.toast-container.error {
  background-color: #f44336;
  color: white;
}

.toast-container.warning {
  background-color: #ff9800;
  color: white;
}

.toast-container.info {
  background-color: #2196f3;
  color: white;
}

.toast-fade-enter-active,
.toast-fade-leave-active {
  transition: all 0.3s ease;
}

.toast-fade-enter,
.toast-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-20px);
}

@media screen and (max-width: 900px) {
  .page-inner {
    min-width: 780px;
    width: max-content;
  }

  .video-processing {
    width: 750px;
    min-width: 750px;
    padding: 15px;
  }

  .content-area {
    width: 720px;
    min-width: 720px;
    flex-wrap: nowrap;
  }

  .user-records {
    width: 750px;
    min-width: 750px;
  }

  .table-scroll {
    overflow-x: auto;
  }

  .video-processing::before {
    content: "👉 手机端可左右滑动查看完整内容，表格可单独横向滑动";
    display: block;
    text-align: center;
    color: #00bcd4;
    font-size: 12px;
    padding: 8px;
    background-color: rgba(0, 188, 212, 0.1);
    border-radius: 4px;
    margin-bottom: 10px;
  }

  .toast-container {
    top: 70px !important;
    min-width: 250px;
    max-width: 90vw;
  }
}

@media screen and (max-width: 600px) {
  .filter-container {
    justify-content: flex-start;
    flex-wrap: nowrap;
  }

  .record-type-filter {
    width: 150px;
  }

  .date-filter {
    width: 120px;
  }
}
</style>
